import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import ExploreCharactersView from '~/components/MoreOneOnOne/ExploreCharacters/ExploreCharactersView';
import { FlexStack, Icon } from '~/components/atomic';
import { FullScreenLoading } from '~/components/common';
import { useGetSearchCharacterListQuery } from '~/features/character';
import {
  useCharacterLikeActions,
  useCreateChatRoom,
  useIntersectionObserver,
} from '~/hooks';
import { PAGE_ROUTE, SECOND } from '~/constants';
import { useUserStore } from '~/store';
import { HeaderWrapper, SubLength, Title } from './SearchResults';

interface CharacterListResultsProps {
  query: string;
  isResultFull?: boolean;
}

export default function CharacterListResults({
  query,
  isResultFull,
}: CharacterListResultsProps) {
  const navigate = useNavigate();

  const { data, isFetchingNextPage, fetchNextPage } =
    useGetSearchCharacterListQuery(
      {
        query,
      },
      10 * SECOND,
    );

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.1,
  });

  useEffect(() => {
    if (isResultFull && isIntersecting) {
      fetchNextPage();
    }
  }, [isIntersecting, isResultFull]);

  const { handleLikeClick } = useCharacterLikeActions({
    query,
  });
  const { handleCreateRoomClick, isCreateRoomPending } = useCreateChatRoom();

  const handleMoreCharacterClick = () => {
    navigate(PAGE_ROUTE.SEARCH_CHARACTER, { state: query });
  };

  const results = isResultFull ? data.pages : data.pages.slice(0, 4);

  return (
    <>
      {isCreateRoomPending && <FullScreenLoading />}
      <FlexStack>
        <HeaderWrapper>
          <Title>One on One</Title>
          <SubLength>{data.total} result</SubLength>
        </HeaderWrapper>

        <div>
          <ExploreCharactersView
            characterList={results}
            handleCardClick={handleCreateRoomClick}
            handleLikeClick={handleLikeClick}
            isFetchingNextPage={isFetchingNextPage}
          />

          {!isResultFull && data.pages.length > 4 && (
            <MoreText onClick={handleMoreCharacterClick}>
              More <Icon iconName='ArrowRight' size={20} />
            </MoreText>
          )}

          {isResultFull && !isFetchingNextPage && <div ref={ref} />}
        </div>
      </FlexStack>
    </>
  );
}

export const MoreText = styled.p`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    height: 44px;
    margin-top: 10px;
    color: ${theme.themeColor.main_light};
    cursor: pointer;
    ${theme.fontStyle.text.body16_medium};
  `}
`;
